import type Store from '@ember-data/store';
import Service, { service } from '@ember/service';
import {
  queryWithPagesAndLocalCache,
  updateQueryCache,
} from 'donor-elf-web/lib/store-utils';
import type ModelRegistry from 'ember-data/types/registries/model';
import { TrackedArray } from 'tracked-built-ins';
import type CurrentUser from './current-user';

export default class CachedDataset extends Service {
  @service declare currentUser: CurrentUser;
  @service declare store: Store;

  modelServices: Record<keyof ModelRegistry, Dataset<keyof ModelRegistry>> = {};

  forModel<K extends keyof ModelRegistry>(modelName: K) {
    let service = this.modelServices[modelName];

    if (!service) {
      service = new Dataset<K>(modelName, this.currentUser, this.store);
      this.modelServices[modelName] = service;
    }

    return service as Dataset<K>;
  }
}

export class Dataset<K extends keyof ModelRegistry> {
  constructor(
    private modelName: K,
    private currentUser: CurrentUser,
    private store: Store,
  ) {}

  private _records: ModelRegistry[K][] = [];
  private _isLoaded = false;
  private _loadingPromise?: Promise<ModelRegistry[K][]>;

  findById(id: string): ModelRegistry[K] {
    return this._records.find((r) => r.id === id);
  }

  async load(options?: {
    pageSize?: number;
    cacheKey?: string;
    cacheLocalWhenRecordLengthGreaterThan?: number;
    reload?: boolean;
  }) {
    if (this._isLoaded && !options?.reload) {
      return this._records;
    }

    this._loadingPromise ??= new Promise(async (resolve, reject) => {
      try {
        await this.loadInternal(options);

        this._isLoaded = true;
        this._loadingPromise = undefined;

        resolve(this._records);
      } catch (error: any) {
        this._loadingPromise = undefined;
        reject(error);
      }
    });

    return this._loadingPromise;
  }

  async add(recordOrId: ModelRegistry[K] | string) {
    const record: ModelRegistry[K] =
      typeof recordOrId === 'string'
        ? await this.store.findRecord<ModelRegistry[K]>(
            this.modelName,
            recordOrId,
          )
        : (recordOrId as ModelRegistry[K]);

    const index = this._records.findIndex((r) => r.id === record.id);

    if (index !== -1) {
      console.error('Record already exists in dataset:', record.id);
      return record;
    }

    this._records.push(record);
    this.updateCache();
  }

  async reload(recordOrId: ModelRegistry[K] | string) {
    const id = typeof recordOrId === 'string' ? recordOrId : recordOrId.id;

    const record = await this.store.findRecord<ModelRegistry[K]>(
      this.modelName,
      id,
    );

    return this.update(record);
  }

  remove(recordOrId: ModelRegistry[K] | string) {
    const record =
      typeof recordOrId === 'string'
        ? this._records.find((r) => r.id === recordOrId)
        : (recordOrId as ModelRegistry[K]);

    const index = this._records.indexOf(record);

    if (index !== -1) {
      this._records.splice(index, 1);
      this.updateCache();
    }
  }

  update(record: ModelRegistry[K]) {
    const index = this._records.findIndex((r) => r.id === record.id);

    if (index !== -1) {
      this._records[index] = record;
    } else {
      this._records.push(record);
    }

    this.updateCache();

    return record;
  }

  private get cacheKey() {
    return `dataset:${this.modelName}?u=${this.currentUser.id}`;
  }

  private async loadInternal(options?: {
    pageSize?: number;
    cacheLocalWhenRecordLengthGreaterThan?: number;
  }) {
    options ??= {};
    options.pageSize ??= 500;
    options.cacheLocalWhenRecordLengthGreaterThan ??= 250;

    const data = await queryWithPagesAndLocalCache<ModelRegistry[K]>({
      cacheKey: this.cacheKey,
      cacheLocalWhenRecordLengthGreaterThan:
        options.cacheLocalWhenRecordLengthGreaterThan,
      store: this.store,
      pageSize: options.pageSize,
      modelName: this.modelName as string,
      onRecordsFromServer: (args) => {
        this._records.splice(0, this._records.length, ...args.records);
      },
    });

    this._records = new TrackedArray(data);
  }

  private updateCache() {
    updateQueryCache(this.modelName, this.cacheKey, this._records);
  }
}
